import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  Stack,
  Container,
  TextField,
  Link,
  Autocomplete,
  CircularProgress,
} from '@mui/material'
import type { Theme } from '@mui/material/styles'
import { Sports, School } from '@mui/icons-material'
import {
  DIVISION_LABELS,
  UserRole,
  isEmailAllowedForSchool,
  SUPPORT_EMAIL,
} from '@tnf-recruit/shared'
import {
  LIMITED_TIME_PRICING_LABEL,
  RECRUITER_PRICE_RANGE_LABEL,
  discountedPriceRangeLabel,
  formatDiscountedRecruiterPrice,
  formatRecruiterPrice,
} from '../lib/pricing'
import { OfferPrice } from '../components/OfferPrice'
import { LaunchOfferChip } from '../components/LaunchOfferChip'
import { useLaunchOffer } from '../hooks/useLaunchOffer'
import { SchoolLogo } from '../components/SchoolLogo'
import { useSchoolDirectory, describeSchool, type PublicSchool } from '../hooks/usePublicSchools'
import { PublicFooter } from '../components/PublicFooter'
import { PublicNavbar } from '../components/PublicNavbar'
import { PublicPageIntro } from '../components/PublicPageIntro'
import { PasswordRequirements } from '../components/PasswordRequirements'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import { isPasswordValid } from '../lib/password'

// Shared field styling: blend the floating label into the grey page (beating the global
// white-label rule in index.css) and keep autofilled inputs on-theme.
const fieldSx = {
  '&& .MuiInputLabel-root': {
    backgroundColor: (theme: Theme) => `${theme.palette.background.default} !important`,
  },
  '& input:-webkit-autofill': {
    WebkitBoxShadow: (theme: Theme) => `0 0 0 1000px ${theme.palette.background.default} inset`,
    WebkitTextFillColor: (theme: Theme) => theme.palette.text.primary,
    caretColor: (theme: Theme) => theme.palette.text.primary,
    borderRadius: 0,
  },
}

export const SignupPage = () => {
  const { signUp } = useAuth()
  const navigate = useNavigate()
  const [selectedRole, setSelectedRole] = useState<UserRole.STUDENT | UserRole.RECRUITER | null>(
    null
  )
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [school, setSchool] = useState<PublicSchool | null>(null)
  const launchOffer = useLaunchOffer()
  const discountPercent = launchOffer?.active ? launchOffer.percentOff : null
  const [schoolQuery, setSchoolQuery] = useState('')
  const formRef = useRef<HTMLDivElement>(null)

  const isRecruiter = selectedRole === UserRole.RECRUITER
  const schools = useSchoolDirectory(schoolQuery, isRecruiter)

  // Same rule the server enforces, run early so a recruiter isn't told their
  // email is wrong only after creating an account they can't use.
  const emailMatchesSchool =
    !school || !email.trim() || isEmailAllowedForSchool(email.trim(), school)

  const handleRoleSelection = (role: UserRole.STUDENT | UserRole.RECRUITER) => {
    setSelectedRole(role)
    setError(null)
  }

  // Reveal the email/password form by scrolling to it once a role is picked.
  useEffect(() => {
    if (selectedRole) {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [selectedRole])

  const handleSignUp = async () => {
    if (!selectedRole) return
    if (!isPasswordValid(password)) {
      setError('Password does not meet all the requirements.')
      return
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }
    if (isRecruiter && !school) {
      setError('Select your school to continue.')
      return
    }
    if (isRecruiter && school && !isEmailAllowedForSchool(email.trim(), school)) {
      setError(
        `That email doesn't belong to ${school.name}. Recruiter accounts need an email address at the school you selected.`
      )
      return
    }
    setError(null)
    setIsSubmitting(true)
    try {
      await signUp({
        email: email.trim(),
        password,
        role: selectedRole,
        // Carried in user metadata so the choice survives the email-confirmation
        // round trip; the server revalidates it against the verified email.
        schoolId: school?.id,
        emailRedirectTo: `${window.location.origin}/login`,
      })
      const { data } = await supabase.auth.getSession()
      if (data.session) {
        navigate('/dashboard', { replace: true })
        return
      }
      setSuccess(true)
    } catch (err: any) {
      setError(err?.message ?? 'Failed to sign up. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const passwordsMismatch = confirmPassword.length > 0 && confirmPassword !== password
  const canSubmit =
    !!email &&
    isPasswordValid(password) &&
    confirmPassword === password &&
    (!isRecruiter || (!!school && emailMatchesSchool))

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <PublicNavbar />
      <Box sx={{ py: 8 }}>
        <Container maxWidth="md">
          <PublicPageIntro
            title="Choose Your Role"
            description="Select how you'll be using T&F All-Star Recruiting."
          />

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                  border: selectedRole === UserRole.STUDENT ? 2 : 1,
                  borderColor: selectedRole === UserRole.STUDENT ? 'primary.main' : 'divider',
                }}
                onClick={() => handleRoleSelection(UserRole.STUDENT)}
              >
                <CardContent sx={{ textAlign: 'center', p: 4 }}>
                  <Sports sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h4" gutterBottom>
                    I'm an Athlete
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    Create your athletic profile and get discovered by college recruiters
                  </Typography>
                  <Box sx={{ mb: 3 }}>
                    <Chip label="First 1,000 free" color="success" sx={{ m: 0.5 }} />
                    <Chip label="Video highlights" color="primary" sx={{ m: 0.5 }} />
                    <Chip label="Academic records" color="primary" sx={{ m: 0.5 }} />
                  </Box>
                  <Button
                    variant={selectedRole === UserRole.STUDENT ? 'contained' : 'outlined'}
                    color={selectedRole === UserRole.STUDENT ? 'warning' : 'primary'}
                    size="large"
                    fullWidth
                  >
                    Sign Up as Athlete
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                  border: selectedRole === UserRole.RECRUITER ? 2 : 1,
                  borderColor: selectedRole === UserRole.RECRUITER ? 'primary.main' : 'divider',
                }}
                onClick={() => handleRoleSelection(UserRole.RECRUITER)}
              >
                <CardContent sx={{ textAlign: 'center', p: 4 }}>
                  <School sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h4" gutterBottom>
                    I'm a College Recruiter
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    Access our database of qualified student-athletes
                  </Typography>
                  <Box sx={{ mb: 3 }}>
                    {/* No school picked yet on this card, so the range is all
                        that can honestly be quoted here. */}
                    <Chip
                      label={
                        <>
                          <OfferPrice
                            original={RECRUITER_PRICE_RANGE_LABEL}
                            discounted={
                              discountPercent
                                ? discountedPriceRangeLabel(discountPercent)
                                : undefined
                            }
                          />
                          /year
                        </>
                      }
                      color="primary"
                      sx={{ m: 0.5 }}
                    />
                    <Chip label={LIMITED_TIME_PRICING_LABEL} color="warning" sx={{ m: 0.5 }} />
                    <LaunchOfferChip offer={launchOffer} sx={{ m: 0.5 }} />
                    <Chip label="Unlimited access" color="primary" sx={{ m: 0.5 }} />
                    <Chip label="Advanced search" color="primary" sx={{ m: 0.5 }} />
                  </Box>
                  <Button
                    variant={selectedRole === UserRole.RECRUITER ? 'contained' : 'outlined'}
                    color={selectedRole === UserRole.RECRUITER ? 'warning' : 'primary'}
                    size="large"
                    fullWidth
                  >
                    Sign Up as Recruiter
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Box sx={{ textAlign: 'center', mt: 4 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Account created. You can now log in.
              </Alert>
            )}
            {selectedRole && (
              <Stack ref={formRef} spacing={2} sx={{ maxWidth: 480, mx: 'auto', mb: 3 }}>
                {isRecruiter && (
                  <Autocomplete
                    options={schools.data ?? []}
                    value={school}
                    loading={schools.isFetching}
                    onChange={(_event, value) => setSchool(value)}
                    onInputChange={(_event, value) => setSchoolQuery(value)}
                    getOptionLabel={option => option.name}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    // The list is already filtered server-side; filtering again
                    // locally would hide results outside the current page.
                    filterOptions={options => options}
                    noOptionsText={
                      schoolQuery ? (
                        <>
                          Can&apos;t find your school? Email{' '}
                          <Link href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Link>
                        </>
                      ) : (
                        'Start typing your school name'
                      )
                    }
                    renderOption={(props, option) => (
                      <Box component="li" {...props} key={option.id} sx={{ gap: 1.5 }}>
                        <SchoolLogo name={option.name} logoUrl={option.logoUrl} size={32} />
                        <Box>
                          <Typography variant="body2">{option.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {describeSchool(option)}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    renderInput={params => (
                      <TextField
                        {...params}
                        required
                        label="Your school"
                        helperText="Search the college directory"
                        sx={fieldSx}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {schools.isFetching ? <CircularProgress size={18} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                )}
                <TextField
                  required
                  label="Email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  error={!emailMatchesSchool}
                  helperText={
                    isRecruiter
                      ? emailMatchesSchool
                        ? email.trim()
                          ? ''
                          : 'Use your school email address'
                        : `Use your ${school?.name} email address`
                      : ''
                  }
                  sx={fieldSx}
                />
                <TextField
                  required
                  label="Password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  sx={fieldSx}
                />
                <PasswordRequirements password={password} />
                <TextField
                  required
                  label="Confirm password"
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  error={passwordsMismatch}
                  helperText={passwordsMismatch ? 'Passwords do not match' : ' '}
                  sx={fieldSx}
                />
                {isRecruiter && school && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                      gap: 2,
                      borderTop: '1px solid',
                      borderColor: 'divider',
                      pt: 1.5,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2">{DIVISION_LABELS[school.division]}</Typography>
                      <Chip size="small" color="warning" label={LIMITED_TIME_PRICING_LABEL} />
                      <LaunchOfferChip offer={launchOffer} size="small" />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                      <OfferPrice
                        original={formatRecruiterPrice(school.division)}
                        discounted={
                          discountPercent
                            ? formatDiscountedRecruiterPrice(school.division, discountPercent)
                            : undefined
                        }
                      />
                      <Typography component="span" variant="caption" color="text.secondary">
                        /yr
                      </Typography>
                    </Typography>
                  </Box>
                )}
                <Button
                  variant="contained"
                  color="warning"
                  onClick={handleSignUp}
                  disabled={isSubmitting || !canSubmit}
                  size="large"
                >
                  {isSubmitting ? 'Creating account...' : `Create ${selectedRole} account`}
                </Button>
              </Stack>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              <Box
                component="span"
                sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.5 }}
              >
                <span>Already have an account?</span>
                <Link
                  component="button"
                  type="button"
                  onClick={() => navigate('/login')}
                  sx={{
                    display: 'inline',
                    p: 0,
                    m: 0,
                    lineHeight: 'inherit',
                    verticalAlign: 'baseline',
                    fontWeight: 700,
                  }}
                >
                  Sign in here
                </Link>
              </Box>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Need help signing up? Email{' '}
              <Link href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Link>
            </Typography>
          </Box>
        </Container>
      </Box>
      <Stack sx={{ mt: 6 }}>
        <PublicFooter />
      </Stack>
    </Box>
  )
}
